import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { Leaf, Car, TreePine } from 'lucide-react';
import { getCo2SavedKg, co2ToDrivingMiles, co2ToTreeMonths } from '../../util/co2Savings';

import css from './EnvironmentalImpactSection.module.css';

/**
 * EnvironmentalImpactSection displays CO2 savings and equivalencies
 * for purchasing a used electronics item instead of new.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS class
 * @param {string} [props.rootClassName] - Override root CSS class
 * @param {Object} props.publicData - Listing public_data containing CO2 info
 * @returns {JSX.Element|null} Environmental impact section or null if no data
 */
const EnvironmentalImpactSection = props => {
  const { className, rootClassName, publicData } = props;

  const co2SavedKg = getCo2SavedKg(publicData);

  // Don't render if no CO2 data available
  if (!co2SavedKg || co2SavedKg <= 0) {
    return null;
  }

  const drivingMiles = co2ToDrivingMiles(co2SavedKg);
  const treeMonths = co2ToTreeMonths(co2SavedKg);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {/* Header */}
      <div className={css.header}>
        <Leaf className={css.headerIcon} />
        <h3 className={css.headerTitle}>
          <FormattedMessage id="EnvironmentalImpact.header" />
        </h3>
      </div>

      {/* Stats */}
      <div className={css.stats}>
        {/* CO2 saved */}
        <div className={css.stat}>
          <Leaf className={css.statIcon} />
          <div className={css.statContent}>
            <div className={css.statValue}>{co2SavedKg} kg</div>
            <div className={css.statLabel}>
              <FormattedMessage id="EnvironmentalImpact.co2Saved" />
            </div>
          </div>
        </div>

        {/* Driving equivalent */}
        {drivingMiles > 0 && (
          <div className={css.stat}>
            <Car className={css.statIcon} />
            <div className={css.statContent}>
              <div className={css.statValue}>{drivingMiles} miles</div>
              <div className={css.statLabel}>
                <FormattedMessage id="EnvironmentalImpact.drivingEquivalent" />
              </div>
            </div>
          </div>
        )}

        {/* Tree equivalent */}
        {treeMonths > 0 && (
          <div className={css.stat}>
            <TreePine className={css.statIcon} />
            <div className={css.statContent}>
              <div className={css.statValue}>{treeMonths} months</div>
              <div className={css.statLabel}>
                <FormattedMessage id="EnvironmentalImpact.treeEquivalent" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <p className={css.disclaimer}>
        <FormattedMessage id="EnvironmentalImpact.disclaimer" />
      </p>
    </div>
  );
};

export default EnvironmentalImpactSection;
